import { getRootEnv } from '@bringg-frontend/bringg-web-infra';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import {
	ServicesSetup,
	ServicesSetupAction,
	ServicesSetupCreateParams
} from '@bringg/dashboard-sdk/dist/Service/Services.consts';

class ServicesSetupStore {
	keys: ServicesSetup[] = [];
	actions: ServicesSetupAction[] = [];
	isFetched = false;
	hasLoadError = false;

	constructor() {
		makeObservable(this, {
			keys: observable.shallow,
			actions: observable.shallow,
			isFetched: observable,
			hasLoadError: observable,
			actionById: computed,
			keyCountByAction: computed,
			fetchAll: action,
			create: action,
			delete: action
		});
	}

	get actionById(): Map<string, ServicesSetupAction> {
		return new Map(this.actions.map(serviceAction => [serviceAction.id, serviceAction]));
	}

	get keyCountByAction(): Map<string, number> {
		return this.keys.reduce((counts, key) => counts.set(key.action, (counts.get(key.action) ?? 0) + 1), new Map());
	}

	fetchAll = async (force = false): Promise<void> => {
		if (this.isFetched && !force) {
			return;
		}

		const { sdk } = getRootEnv().dashboardSdk;

		try {
			// The catalogue only feeds the endpoint label, entity and picker, so losing it degrades the
			// table rather than emptying it.
			let hasCatalogue = true;
			const [keys, actions] = await Promise.all([
				sdk.service.getAll(),
				sdk.service.listActions().catch(() => {
					hasCatalogue = false;

					return [] as ServicesSetupAction[];
				})
			]);

			runInAction(() => {
				this.keys = keys ?? [];
				this.actions = actions ?? [];
				this.hasLoadError = false;
				// The store outlives the page, so a partial or failed load must not stick: the next visit
				// retries instead of keeping Create disabled or showing a stale error over loaded keys.
				this.isFetched = hasCatalogue;
			});
		} catch {
			runInAction(() => {
				this.hasLoadError = true;
				this.isFetched = false;
			});
		}
	};

	// A rejected action throws out of the SDK rather than resolving an error body, so there is no
	// unsuccessful response to branch on here.
	create = async (params: ServicesSetupCreateParams): Promise<ServicesSetup> => {
		const { service: created } = await getRootEnv().dashboardSdk.sdk.service.create(params);

		runInAction(() => {
			this.keys = [...this.keys, created];
		});

		return created;
	};

	delete = async (id: number): Promise<void> => {
		const response = await getRootEnv().dashboardSdk.sdk.service.delete(id);

		if (!response?.success) {
			throw new Error();
		}

		runInAction(() => {
			this.keys = this.keys.filter(key => key.id !== id);
		});
	};

	reset = () => {
		this.keys = [];
		this.actions = [];
		this.isFetched = false;
		this.hasLoadError = false;
	};
}

export default ServicesSetupStore;
